import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {Table} from '@core/components/table/table';
import {DashboardTaskRow, TableColumn, TaskItem, TaskPriority, TaskStatus} from '@core/types';
import {NgTemplateOutlet} from '@angular/common';
import {DatePicker, Select, TaskAttachments} from '@core/components';
import {FormsModule} from '@angular/forms';
import moment from 'moment-timezone';

type TaskEditModel = {
  dateRange?: {start: string; end: string} | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeId?: string | number | null;
};

@Component({
  selector: 'core-task-table',
  imports: [
    Table,
    NgTemplateOutlet,
    DatePicker,
    Select,
    TaskAttachments,
    FormsModule
  ],
  templateUrl: './task-table.html',
  styleUrl: './task-table.scss',
  standalone: true,
  host: {
    style: 'display:flex;height: 100%;width: 100%;overflow-x:hidden;overflow-y:auto;padding-right: 0.5rem'
  }
})
export class TaskTable implements OnInit {

  @Input()
  expanded: boolean = false;

  @ViewChild('taskDescriptionTpl', {static: true})
  tableTaskDescription!: TemplateRef<any>;

  @ViewChild('taskPriorityTagTpl', {static: true})
  tablePriorityTag!: TemplateRef<any>;

  @ViewChild('taskStatusTagTpl', {static: true})
  tableStatusTag!: TemplateRef<any>;

  headers: TableColumn<any>[] = [];
  data: TaskItem[] = [];
  editModels: {[key: string]: TaskEditModel} = {};

  @Input()
  assigneeOptions: {label: string; value: string | number}[] = [];

  @Input()
  statusOptions: {label: string; value: string}[] = [];

  @Input()
  priorityOptions: {label: string; value: string}[] = [];

  @Output()
  saveTask = new EventEmitter<{taskId: string | number; updates: {status?: string; priority?: string; startDate?: string | null; endDate?: string | null}; assigneeId?: string | number | null}>();

  @Output()
  deleteTask = new EventEmitter<TaskItem>();

  @Output()
  editTask = new EventEmitter<TaskItem>();

  @Output()
  taskUpdated = new EventEmitter<TaskItem>();

  ngOnInit() {
    const headers: TableColumn<any>[] = [
      {  label: 'Tarea', slug: 'task', customTemplate: this.tableTaskDescription},
      {  label: 'Fecha Límite', slug: 'date', field: 'date', options: {verticalAlign: 'middle'}},
      {  label: 'Implicados', slug: 'responsibles', field: 'responsibles', options: {verticalAlign: 'middle'}},
      {  label: 'Estado', slug: 'status', customTemplate: this.tableStatusTag, options: {verticalAlign: 'middle'}}
    ];

    const summary = ['task', 'status'];
    const  expanded = ['task', 'date', 'responsibles' ,'status'];

    const fields = this.expanded ? expanded : summary;

    this.headers = headers.filter(header => header.slug && fields.includes(header.slug));
  }

  @Input()
  set tasks(value: Array<TaskItem | DashboardTaskRow> | null | undefined) {
    if (value) {
      this.data = value.map(item => {
        const normalized = this.normalizeTask(item as TaskItem);
        const existing = this.data.find(candidate => candidate.id === normalized.id);
        if (existing) {
          Object.assign(existing, normalized);
          return existing;
        }
        return normalized;
      });
    }
  }

  getStatusLabel(value?: string) {
    if (!value) return '';
    return this.statusOptions.find(option => option.value === value)?.label || value;
  }

  getPriorityLabel(value?: string) {
    if (!value) return '';
    return this.priorityOptions.find(option => option.value === value)?.label || value;
  }

  getEditModel(item: TaskItem): TaskEditModel {
    const key = String(item.id);
    if (!this.editModels[key]) {
      this.editModels[key] = {
        dateRange: item.startDate && item.endDate ? {start: item.startDate, end: item.endDate} : null,
        status: item.status || 'open',
        priority: item.priority || 'medium',
        assigneeId: item.assignedTo?.id || item.assignees?.[0]?.id || null
      };
    }
    return this.editModels[key];
  }

  handleSave(item: TaskItem) {
    const model = this.getEditModel(item);
    this.applyLocalModel(item, model);
    const updates: {status?: string; priority?: string; startDate?: string | null; endDate?: string | null} = {};
    if (model.status) updates.status = model.status;
    if (model.priority) updates.priority = model.priority;
    if (model.dateRange) {
      updates.startDate = model.dateRange.start || null;
      updates.endDate = model.dateRange.end || null;
    }
    this.saveTask.emit({
      taskId: item.id,
      updates,
      assigneeId: model.assigneeId || null
    });
  }

  handleDelete(item: TaskItem) {
    this.deleteTask.emit(item);
  }

  handleEdit(item: TaskItem) {
    this.editTask.emit(item);
  }

  handleAttachmentUpdated(task: TaskItem) {
    this.taskUpdated.emit(task);
  }

  private applyLocalModel(item: TaskItem, model: TaskEditModel) {
    item.status = model.status || item.status;
    item.priority = model.priority || item.priority;

    if (model.dateRange) {
      item.startDate = model.dateRange.start || undefined;
      item.endDate = model.dateRange.end || undefined;
      const rawDate = item.endDate || item.startDate || '';
      item.date = rawDate ? moment(rawDate).format('DD/MM/YYYY') : '';
    }

    if (model.assigneeId) {
      const assignee = this.assigneeOptions.find(option => option.value === model.assigneeId);
      if (assignee) {
        item.assignedTo = {id: model.assigneeId, name: assignee.label};
        item.responsibles = assignee.label;
      }
    }
  }

  private normalizeTask(item: TaskItem): TaskItem {
    const responsibles = item.responsibles || item.assignees?.map(person => person.name).join(', ') || item.assignedTo?.name || '';
    const rawDate = item.date || item.endDate || item.startDate || '';
    const date = rawDate ? moment(rawDate).format('DD/MM/YYYY') : '';
    return {
      ...item,
      responsibles,
      date
    };
  }
}
